/**
 * Vet-facing clinical record editor for a single booking. Loads any existing
 * record, lets the vet edit a DRAFT (diagnosis, notes, vitals, prescription
 * lines), Save draft (create/update) or Finalize (locks it, owner-visible).
 * Read-only once FINALIZED. Esc / backdrop closes.
 */
import { useEffect, useRef, useState } from 'react';
import { FileImage, Loader2, Plus, Trash2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { AuthImage, open_file_in_new_tab } from '@/components/auth-image';
import { form_error_message } from '@/lib/forms';
import { use_visit_record, use_save_record, use_record_pet_options } from '@/features/medical-record/hooks';
import { PRESCRIPTION_FORMS, type PrescriptionForm, type PrescriptionItemInput } from '@/features/medical-record/types';
import type { Booking } from '@/features/bookings/types';

type ItemDraft = {
  drug_name: string;
  strength: string;
  form: PrescriptionForm;
  dosage: string;
  frequency: string;
  duration_days: string;
  instructions: string;
};

const EMPTY_ITEM: ItemDraft = { drug_name: '', strength: '', form: 'TABLET', dosage: '', frequency: '', duration_days: '', instructions: '' };

const field_class =
  'flex h-11 w-full rounded-input border-2 border-input bg-card px-3 py-2 text-sm text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:outline-none focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15 disabled:opacity-60';

export function VisitRecordModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const record_q = use_visit_record(booking.id);
  const { create_m, update_m, finalize_m, upload_doc_m, delete_doc_m } = use_save_record(booking.id);
  const file_input_ref = useRef<HTMLInputElement>(null);
  const [doc_error, set_doc_error] = useState<string | null>(null);

  const [chief_complaint, set_chief_complaint] = useState('');
  const [diagnosis, set_diagnosis] = useState('');
  const [clinical_notes, set_clinical_notes] = useState('');
  const [weight_kg, set_weight_kg] = useState('');
  const [follow_up_on, set_follow_up_on] = useState('');
  const [advice, set_advice] = useState('');
  const [items, set_items] = useState<ItemDraft[]>([]);
  const [error, set_error] = useState<string | null>(null);
  const [pet_id, set_pet_id] = useState('');

  const record = record_q.data ?? null;
  const exists = record !== null;
  const finalized = record?.status === 'FINALIZED';
  const read_only = finalized;
  // Booked without a specific pet. The record is keyed to a pet, so the vet picks one
  // from the owner's pets — creating the record back-fills it onto the booking.
  const no_pet = !booking.pet_id && !exists;
  const pet_options_q = use_record_pet_options(booking.id, no_pet);
  const pet_options = pet_options_q.data ?? [];
  const owner_has_no_pets = pet_options_q.isSuccess && pet_options.length === 0;
  // Blocked only when the owner has no pets at all — otherwise the picker resolves it.
  const pet_blocked = no_pet && owner_has_no_pets;
  /** Saving is gated until a pet is resolved: either the booking has one or the vet picks one. */
  const pet_missing = no_pet && !pet_id;

  // Hydrate the form once the record query resolves.
  useEffect(() => {
    if (record_q.isLoading) return;
    set_chief_complaint(record?.chief_complaint ?? '');
    set_diagnosis(record?.diagnosis ?? '');
    set_clinical_notes(record?.clinical_notes ?? '');
    set_weight_kg(record?.weight_kg ?? '');
    set_follow_up_on(record?.follow_up_on ?? '');
    set_advice(record?.prescription?.advice ?? '');
    set_items(
      (record?.prescription?.items ?? []).map((i) => ({
        drug_name: i.drug_name,
        strength: i.strength ?? '',
        form: i.form,
        dosage: i.dosage ?? '',
        frequency: i.frequency ?? '',
        duration_days: i.duration_days != null ? String(i.duration_days) : '',
        instructions: i.instructions ?? '',
      })),
    );
  }, [record, record_q.isLoading]);

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  function patch_item(idx: number, patch: Partial<ItemDraft>) {
    set_items((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function build_payload() {
    const clean_items: PrescriptionItemInput[] = items
      .filter((i) => i.drug_name.trim().length > 0)
      .map((i) => ({
        drug_name: i.drug_name.trim(),
        strength: i.strength.trim() || undefined,
        form: i.form,
        dosage: i.dosage.trim() || undefined,
        frequency: i.frequency.trim() || undefined,
        duration_days: i.duration_days.trim() ? Number(i.duration_days) : undefined,
        instructions: i.instructions.trim() || undefined,
      }));
    return {
      // Only meaningful on create for a petless booking; the server ignores it otherwise.
      pet_id: no_pet ? pet_id || undefined : undefined,
      chief_complaint: chief_complaint.trim() || undefined,
      diagnosis: diagnosis.trim() || undefined,
      clinical_notes: clinical_notes.trim() || undefined,
      weight_kg: weight_kg.trim() ? Number(weight_kg) : undefined,
      follow_up_on: follow_up_on || undefined,
      prescription: clean_items.length > 0 || advice.trim() ? { advice: advice.trim() || undefined, items: clean_items } : undefined,
    };
  }

  async function save_draft() {
    set_error(null);
    try {
      await (exists ? update_m.mutateAsync(build_payload()) : create_m.mutateAsync(build_payload()));
    } catch (err) {
      set_error(form_error_message(err, 'Could not save the record.'));
    }
  }

  async function save_and_finalize() {
    set_error(null);
    try {
      await (exists ? update_m.mutateAsync(build_payload()) : create_m.mutateAsync(build_payload()));
      await finalize_m.mutateAsync();
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'Could not finalize the record.'));
    }
  }

  async function on_pick_doc(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    set_doc_error(null);
    try {
      await upload_doc_m.mutateAsync(file);
    } catch (err) {
      set_doc_error(form_error_message(err, 'Could not upload the prescription.'));
    }
  }

  const docs = record?.prescription_documents ?? [];
  const busy = create_m.isPending || update_m.isPending || finalize_m.isPending;
  const can_finalize = booking.status === 'COMPLETED' && diagnosis.trim().length > 0 && !read_only && !pet_missing;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="visit-record-modal"
      onClick={on_close}
    >
      <div
        className="my-8 w-full max-w-2xl rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h2 className="font-heading text-lg font-bold">Visit record</h2>
            <p className="text-xs text-muted-foreground">
              {finalized ? 'Finalized — read only' : exists ? 'Draft — editable' : 'New draft'}
            </p>
          </div>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="visit-record-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {record_q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-5">
            {no_pet && (
              pet_blocked ? (
                <p className="rounded-md border border-warning/25 bg-warning/10 p-2 text-xs text-warning" data-testid="visit-record-no-pet">
                  This booking has no pet attached, and the customer has no pets on their
                  profile yet. Ask them to add one before you record the visit.
                </p>
              ) : (
                <div className="flex flex-col gap-1.5 rounded-md border border-warning/25 bg-warning/10 p-3" data-testid="visit-record-pick-pet">
                  <Label htmlFor="vr-pet">Which pet is this visit for?</Label>
                  <p className="text-xs text-muted-foreground">
                    This visit was booked without a specific pet. Pick one to record against —
                    it will be attached to the booking too.
                  </p>
                  <SelectField
                    id="vr-pet"
                    data-testid="vr-pet"
                    value={pet_id}
                    onValueChange={set_pet_id}
                    disabled={read_only || pet_options_q.isLoading}
                    options={[
                      { value: '', label: pet_options_q.isLoading ? 'Loading pets…' : '— Select a pet —' },
                      ...pet_options.map((p) => ({
                        value: p.id,
                        label: `${p.name} · ${p.species.toLowerCase()}`,
                      })),
                    ]}
                  />
                </div>
              )
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Chief complaint</Label>
                <Input value={chief_complaint} onChange={(e) => set_chief_complaint(e.target.value)} disabled={read_only} data-testid="vr-chief" />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Diagnosis</Label>
                <textarea
                  className={`${field_class} h-20 resize-y`}
                  value={diagnosis}
                  onChange={(e) => set_diagnosis(e.target.value)}
                  disabled={read_only}
                  data-testid="vr-diagnosis"
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Clinical notes</Label>
                <textarea
                  className={`${field_class} h-20 resize-y`}
                  value={clinical_notes}
                  onChange={(e) => set_clinical_notes(e.target.value)}
                  disabled={read_only}
                  data-testid="vr-notes"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Weight (kg)</Label>
                <Input type="number" step="0.01" min="0" value={weight_kg} onChange={(e) => set_weight_kg(e.target.value)} disabled={read_only} data-testid="vr-weight" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Follow-up date</Label>
                <DatePicker value={follow_up_on} onChange={set_follow_up_on} disabled={read_only} data-testid="vr-followup" placeholder="Follow-up date" />
              </div>
            </div>

            {/* Prescription */}
            <div className="rounded-input border-2 border-border/60 p-3">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Prescription</h3>
                {!read_only && (
                  <Button size="sm" variant="ghost" onClick={() => set_items((p) => [...p, { ...EMPTY_ITEM }])} data-testid="vr-add-item">
                    <Plus className="h-4 w-4" /> Add drug
                  </Button>
                )}
              </div>

              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground">No drugs added.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {items.map((it, idx) => (
                    <li key={idx} className="rounded-md bg-muted/40 p-2" data-testid={`vr-item-${idx}`}>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <input className={`${field_class} col-span-2 sm:col-span-2`} placeholder="Drug name" value={it.drug_name} onChange={(e) => patch_item(idx, { drug_name: e.target.value })} disabled={read_only} data-testid={`vr-item-drug-${idx}`} />
                        <input className={field_class} placeholder="Strength" value={it.strength} onChange={(e) => patch_item(idx, { strength: e.target.value })} disabled={read_only} />
                        <SelectField
                          className="text-sm"
                          value={it.form}
                          onValueChange={(v) => patch_item(idx, { form: v as PrescriptionForm })}
                          disabled={read_only}
                          options={PRESCRIPTION_FORMS.map((f) => ({ value: f, label: f.toLowerCase() }))}
                        />
                        <input className={field_class} placeholder="Dosage (1 tablet)" value={it.dosage} onChange={(e) => patch_item(idx, { dosage: e.target.value })} disabled={read_only} />
                        <input className={field_class} placeholder="Frequency (BID)" value={it.frequency} onChange={(e) => patch_item(idx, { frequency: e.target.value })} disabled={read_only} />
                        <input className={field_class} type="number" min="1" placeholder="Days" value={it.duration_days} onChange={(e) => patch_item(idx, { duration_days: e.target.value })} disabled={read_only} />
                        <input className={`${field_class} col-span-2 sm:col-span-3`} placeholder="Instructions (after food)" value={it.instructions} onChange={(e) => patch_item(idx, { instructions: e.target.value })} disabled={read_only} />
                        {!read_only && (
                          <button type="button" onClick={() => set_items((p) => p.filter((_, i) => i !== idx))} aria-label="Remove drug" className="flex items-center justify-center rounded-md text-destructive hover:bg-destructive/10" data-testid={`vr-item-remove-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-3 flex flex-col gap-1.5">
                <Label>Advice</Label>
                <textarea className={`${field_class} h-16 resize-y`} value={advice} onChange={(e) => set_advice(e.target.value)} disabled={read_only} data-testid="vr-advice" />
              </div>

              {/* Uploaded prescription photos (scanned/handwritten Rx) */}
              <div className="mt-3 flex flex-col gap-2" data-testid="vr-prescription-docs">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5">
                    <FileImage className="h-4 w-4" /> Prescription photos
                  </Label>
                  {!read_only && (
                    <>
                      <input
                        ref={file_input_ref}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={on_pick_doc}
                        data-testid="vr-doc-input"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => file_input_ref.current?.click()}
                        disabled={!exists || upload_doc_m.isPending || docs.length >= 5}
                        title={!exists ? 'Save the draft first, then attach photos' : docs.length >= 5 ? 'Up to 5 photos' : undefined}
                        data-testid="vr-doc-upload"
                      >
                        {upload_doc_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                      </Button>
                    </>
                  )}
                </div>
                {!exists && !read_only && (
                  <p className="text-xs text-muted-foreground">Save the draft first, then attach prescription photos.</p>
                )}
                {docs.length === 0 ? (
                  exists && <p className="text-xs text-muted-foreground">No prescription photos attached.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {docs.map((d) => (
                      <div key={d.id} className="relative" data-testid={`vr-doc-${d.id}`}>
                        <button
                          type="button"
                          onClick={() => open_file_in_new_tab(d.full_path)}
                          className="block h-20 w-20 overflow-hidden rounded-input border-2 border-border/60"
                          title="Open full size"
                        >
                          <AuthImage path={d.thumb_path} alt="Prescription" className="h-full w-full object-cover" />
                        </button>
                        {!read_only && (
                          <button
                            type="button"
                            onClick={() => delete_doc_m.mutate(d.id)}
                            disabled={delete_doc_m.isPending}
                            aria-label="Remove prescription photo"
                            data-testid={`vr-doc-remove-${d.id}`}
                            className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-1 text-destructive-foreground shadow"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {doc_error && <p className="text-xs text-destructive" data-testid="vr-doc-error">{doc_error}</p>}
              </div>
            </div>

            {error && <p className="text-sm text-destructive" data-testid="visit-record-error">{error}</p>}

            {!read_only && (
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={save_draft} disabled={busy || pet_missing} data-testid="visit-record-save">
                  {busy && !finalize_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save draft
                </Button>
                <Button variant="gradient" onClick={save_and_finalize} disabled={busy || !can_finalize} data-testid="visit-record-finalize" title={booking.status !== 'COMPLETED' ? 'Complete the visit first' : undefined}>
                  {finalize_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Finalize
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
